import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';

const CATEGORIES = [
  { value: 'general',   label: 'General',    color: '#6366f1' },
  { value: 'order',     label: 'Order',      color: '#10b981' },
  { value: 'quote',     label: 'Quote',      color: '#f59e0b' },
  { value: 'follow_up', label: 'Follow Up',  color: '#0891b2' },
];

const catColor = (cat) => CATEGORIES.find(c => c.value === cat)?.color || '#6366f1';
const catLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || cat;

const defaultForm = { name: '', subject: '', body_html: '', category: 'general' };

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, mode: 'add', item: null });
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sendModal, setSendModal] = useState({ open: false, template: null, to_email: '', sending: false, sent: false, error: '' });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, lRes] = await Promise.all([
        api.get('/email-templates'),
        api.get('/email-templates/logs'),
      ]);
      setTemplates(tRes.data.data || []);
      setLogs(lRes.data.data || []);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(defaultForm);
    setPreview(false);
    setModal({ open: true, mode: 'add', item: null });
  };

  const openEdit = (tpl) => {
    setForm({ name: tpl.name, subject: tpl.subject, body_html: tpl.body_html, category: tpl.category });
    setPreview(false);
    setModal({ open: true, mode: 'edit', item: tpl });
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body_html.trim()) {
      return;
    }
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        await api.post('/email-templates', form);
      } else {
        await api.put(`/email-templates/${modal.item.id}`, form);
      }
      setModal({ open: false });
      await load();
    } catch {
      // silently fail — user can retry
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/email-templates/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      await load();
    } catch {
      setDeleteConfirm(null);
    }
  };

  const openSend = (tpl) => {
    setSendModal({ open: true, template: tpl, to_email: '', sending: false, sent: false, error: '' });
  };

  const handleSend = async () => {
    if (!sendModal.to_email.trim()) return;
    setSendModal(s => ({ ...s, sending: true, error: '' }));
    try {
      await api.post('/email-templates/send', {
        template_id: sendModal.template.id,
        to_email: sendModal.to_email.trim(),
      });
      setSendModal(s => ({ ...s, sending: false, sent: true }));
      await load(); // refresh logs
    } catch (err) {
      const msg = err.response?.data?.message || 'Send failed';
      setSendModal(s => ({ ...s, sending: false, error: msg }));
    }
  };

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Email Templates</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Manage reusable email templates with variable substitution
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          + New Template
        </button>
      </div>

      {error && <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16, fontSize: '0.875rem' }}>{error}</div>}

      {/* Template Cards */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <div className="spinner" />
        </div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          No templates yet. Click <strong>+ New Template</strong> to create one.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {templates.map(tpl => (
            <div key={tpl.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', flex: 1 }}>{tpl.name}</div>
                <span style={{
                  display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                  fontSize: '0.72rem', fontWeight: 700,
                  background: catColor(tpl.category) + '20',
                  color: catColor(tpl.category),
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {catLabel(tpl.category)}
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tpl.subject}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button className="btn btn-sm btn-primary" onClick={() => openEdit(tpl)}>Edit</button>
                <button className="btn btn-sm btn-secondary" onClick={() => openSend(tpl)}>Send Test</button>
                <button className="btn btn-sm" style={{ marginLeft: 'auto', color: 'var(--error)', border: '1px solid var(--error)', background: 'transparent' }}
                  onClick={() => setDeleteConfirm(tpl)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Logs */}
      <div style={{ marginTop: 36 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 14 }}>Recent Email Logs</h3>
        {logs.length === 0 ? (
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', padding: '12px 0' }}>No emails sent yet.</div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {logs.map((log, i) => (
              <div key={log.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                borderBottom: i < logs.length - 1 ? '1px solid var(--border)' : 'none',
                fontSize: '0.82rem',
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: log.status === 'sent' ? '#10b981' : '#ef4444',
                }} />
                <span style={{ flex: 1, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.subject}
                </span>
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{log.to_email}</span>
                <span style={{ color: 'var(--text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {new Date(log.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={modal.open}
        onClose={() => setModal({ open: false })}
        title={modal.mode === 'add' ? 'New Template' : 'Edit Template'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal({ open: false })}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Template'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Template Name *</label>
              <input className="form-control" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Order Confirmation" />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Subject *</label>
            <input className="form-control" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Use {{variable_name}} for dynamic values" />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label className="form-label" style={{ margin: 0 }}>Body HTML *</label>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setPreview(p => !p)}
                style={{ fontSize: '0.78rem' }}
              >
                {preview ? 'Edit' : 'Preview'}
              </button>
            </div>
            {preview ? (
              <div style={{
                border: '1px solid var(--border)', borderRadius: 8, padding: 16,
                minHeight: 200, background: '#fff', fontSize: '0.875rem', lineHeight: 1.6,
              }}
                dangerouslySetInnerHTML={{ __html: form.body_html }}
              />
            ) : (
              <textarea
                className="form-control"
                value={form.body_html}
                onChange={e => setForm(p => ({ ...p, body_html: e.target.value }))}
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                placeholder="<p>Dear {{client_name}},</p><p>Your message here...</p>"
              />
            )}
          </div>

          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 6 }}>
            Use <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3 }}>{'{{variable_name}}'}</code> syntax for dynamic content.
            Common variables: <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3 }}>client_name</code>,{' '}
            <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3 }}>order_number</code>,{' '}
            <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3 }}>total</code>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Template"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
          </>
        }
      >
        <p>Delete template <strong>{deleteConfirm?.name}</strong>? This cannot be undone.</p>
      </Modal>

      {/* Send Test Modal */}
      <Modal
        open={sendModal.open}
        onClose={() => setSendModal({ open: false })}
        title={`Send Test — ${sendModal.template?.name || ''}`}
        footer={
          sendModal.sent ? (
            <button className="btn btn-primary" onClick={() => setSendModal({ open: false })}>Close</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setSendModal({ open: false })}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSend} disabled={sendModal.sending || !sendModal.to_email.trim()}>
                {sendModal.sending ? 'Sending…' : 'Send'}
              </button>
            </>
          )
        }
      >
        {sendModal.sent ? (
          <div style={{ textAlign: 'center', padding: '16px 0', color: '#10b981', fontWeight: 600 }}>
            Email sent successfully to {sendModal.to_email}
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Send this template as-is to test it. No variable substitution will be applied.
            </p>
            <div className="form-group">
              <label className="form-label">Recipient Email *</label>
              <input
                className="form-control"
                type="email"
                value={sendModal.to_email}
                onChange={e => setSendModal(s => ({ ...s, to_email: e.target.value, error: '' }))}
                placeholder="test@example.com"
                autoFocus
              />
            </div>
            {sendModal.error && (
              <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: '0.8rem', marginTop: 8 }}>
                {sendModal.error}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
